// Career Map — sphere display metadata (Stage 1).
//
// The cluster→sphere MAPPING lives in src/data/career_map_spheres.json
// (pure data; the scoring pipeline never reads it, but the verify script
// in scripts/verify-career-map.mjs asserts it stays consistent with the
// careers_1822_*.json data — 23 clusters → 10 spheres, Σ = 290, each
// cluster in exactly one sphere).
//
// This file holds the *presentation* layer only: display order, localized
// sphere names, short descriptor subtitles, and an icon key. Counts and the
// example-profession lists are NOT here — they are derived from the career
// data at build/SSR time (see src/careers/data.ts) so they can never drift.
//
// RU/EN sphere names and all descriptor subtitles are proposals pending
// review (per ТЗ: «RU/EN переводы названий сфер — предложить в PR»).

export type Lang = "az" | "ru" | "en";

export const LANGS: Lang[] = ["az", "ru", "en"];

export type SphereMeta = {
  /** URL slug — matches career_map_spheres.json. */
  slug: string;
  /** Icon key resolved by <SphereIcon> in CareersChrome. */
  icon: string;
  /** Localized sphere name. AZ is canonical (from the mockup order). */
  name: Record<Lang, string>;
  /** Short descriptor shown after the count, e.g. "N peşə · <subtitle>". */
  subtitle: Record<Lang, string>;
};

// Order === display order on the hub grid (matches hub_10_spheres mockup:
// 3×3 grid, then a 4th row with Aqro + the test-CTA block).
export const SPHERES: SphereMeta[] = [
  {
    slug: "technology-it",
    icon: "code",
    name: { az: "Texnologiya və IT", ru: "Технологии и IT", en: "Technology & IT" },
    subtitle: {
      az: "rəqəmsal məhsullar, data və şəbəkələr",
      ru: "цифровые продукты, данные и сети",
      en: "digital products, data and networks",
    },
  },
  {
    slug: "finance-business",
    icon: "finance",
    name: { az: "Maliyyə və biznes", ru: "Финансы и бизнес", en: "Finance & Business" },
    subtitle: {
      az: "maliyyə, idarəetmə və sahibkarlıq",
      ru: "финансы, управление и предпринимательство",
      en: "finance, management and entrepreneurship",
    },
  },
  {
    slug: "marketing-sales",
    icon: "marketing",
    name: { az: "Marketinq və satış", ru: "Маркетинг и продажи", en: "Marketing & Sales" },
    subtitle: {
      az: "brend, tələb və satış",
      ru: "бренд, спрос и продажи",
      en: "brand, demand and sales",
    },
  },
  {
    slug: "engineering-construction-energy",
    icon: "engineering",
    name: {
      az: "Mühəndislik, tikinti və enerji",
      ru: "Инженерия, строительство и энергетика",
      en: "Engineering, Construction & Energy",
    },
    subtitle: {
      az: "layihələndirmə, tikinti və enerji",
      ru: "проектирование, стройка и энергетика",
      en: "design, construction and energy",
    },
  },
  {
    slug: "healthcare",
    icon: "health",
    name: { az: "Səhiyyə və tibb", ru: "Здоровье и медицина", en: "Healthcare & Medicine" },
    subtitle: {
      az: "sağlamlıq, müalicə və qayğı",
      ru: "здоровье, лечение и забота",
      en: "health, treatment and care",
    },
  },
  {
    slug: "education-science",
    icon: "education",
    name: { az: "Təhsil və elm", ru: "Образование и наука", en: "Education & Science" },
    subtitle: {
      az: "tədris, tədqiqat və bilik",
      ru: "обучение, исследования и знание",
      en: "teaching, research and knowledge",
    },
  },
  {
    slug: "creative-media",
    icon: "creative",
    name: { az: "Yaradıcılıq və media", ru: "Творчество и медиа", en: "Creative & Media" },
    subtitle: {
      az: "dizayn, incəsənət və media",
      ru: "дизайн, искусство и медиа",
      en: "design, art and media",
    },
  },
  {
    slug: "government-law-social",
    icon: "law",
    name: {
      az: "Dövlət, hüquq və ictimai sahə",
      ru: "Государство, право и общество",
      en: "Government, Law & Society",
    },
    subtitle: {
      az: "hüquq, idarəetmə və cəmiyyət",
      ru: "право, управление и общество",
      en: "law, governance and society",
    },
  },
  {
    slug: "services-tourism-sport",
    icon: "services",
    name: { az: "Xidmət, turizm və idman", ru: "Услуги, туризм и спорт", en: "Services, Tourism & Sport" },
    subtitle: {
      az: "qonaqpərvərlik, səyahət və idman",
      ru: "гостеприимство, путешествия и спорт",
      en: "hospitality, travel and sport",
    },
  },
  {
    slug: "agro-ecology",
    icon: "agro",
    name: { az: "Aqro və ekologiya", ru: "Агро и экология", en: "Agriculture & Ecology" },
    subtitle: {
      az: "kənd təsərrüfatı və ətraf mühit",
      ru: "сельское хозяйство и экология",
      en: "agriculture and environment",
    },
  },
];

export const SPHERE_SLUGS = SPHERES.map((s) => s.slug);

export function getSphereMeta(slug: string): SphereMeta | undefined {
  return SPHERES.find((s) => s.slug === slug);
}

// ── UI strings for the section (per locale) ─────────────────────────────
// Centralized so the hub, sphere pages, nav, hero button and result-page
// link stay consistent. Brand-neutral, review-pending for RU/EN.
export const UI: Record<
  Lang,
  {
    navCareers: string; // nav item replacing «Niyə Yolutap»
    heroDiscover: string; // 3rd hero button on the landing
    resultAllCareers: string; // link on the test-result page
    hubTitle: string; // hero H1
    hubSubtitle: (careers: number, spheres: number) => string;
    searchPlaceholder: string;
    searchButton: string;
    spheresHeading: string;
    seeSphere: string; // card "Sahəyə bax →"
    professionsWord: (n: number) => string; // "N peşə"
    hubCtaTitle: string;
    hubCtaBody: string;
    takeTest: string;
    breadcrumbRoot: string;
    sphereStagedNote: string; // caption under the chip list
    sphereCtaTitle: string;
    sphereCtaBody: string;
    searchNoResults: string;
    searchNoResultsHint: string; // 2nd line: suggest a different spelling
    searchInSphere: string; // "→ <sphere>"
    /** Указатель профессий на каталоге — заголовок и оговорка про охват. */
    indexHeading: string;
    indexNote: string;
    footerCareers: string; // ссылка «Профессии» в футере
    metaTitleHub: string;
    metaDescHub: string;
  }
> = {
  az: {
    navCareers: "Peşələr",
    heroDiscover: "Peşələri kəşf et",
    resultAllCareers: "Bütün peşələrə bax →",
    hubTitle: "Karyera xəritəsi",
    hubSubtitle: (_c, s) =>
      `${s} sahə · 3 dildə. Hər peşə haqqında: nə ilə məşğul olur, hansı bacarıqlar lazımdır, təhsil yolu və kimə uyğundur.`,
    searchPlaceholder: "Peşə axtar: məsələn, «data analitik»",
    searchButton: "Axtar",
    spheresHeading: "Sahələr üzrə",
    seeSphere: "Sahəyə bax →",
    professionsWord: (n) => `${n} peşə`,
    hubCtaTitle: "Hansı peşələr məhz sənə uyğundur?",
    hubCtaBody: "Yolutap diaqnostikasını keç — xəritədə sənin peşələrin işıqlanacaq.",
    takeTest: "Testi keç",
    breadcrumbRoot: "Peşələr",
    sphereStagedNote: "Peşələrin ətraflı səhifələri mərhələli şəkildə əlavə olunur.",
    sphereCtaTitle: "Bu sahə sənə uyğundurmu?",
    sphereCtaBody: "Diaqnostikanı keç — uyğunluq faizini hər peşə üçün görəcəksən.",
    searchNoResults: "Heç nə tapılmadı",
    searchNoResultsHint: "Başqa yazılışı ilə yoxlayın",
    searchInSphere: "sahədə",
    indexHeading: "Bütün peşə səhifələri",
    indexNote:
      "Ətraflı səhifəsi olan peşələr. Siyahı mərhələli şəkildə genişlənir — qalan peşələr öz sahəsinin səhifəsində sadalanır.",
    footerCareers: "Peşələr",
    metaTitleHub: "Peşələr · Karyera xəritəsi — Yolutap",
    metaDescHub:
      "10 sahə üzrə peşələr, 3 dildə. Hər peşə üçün: nə ilə məşğul olur, hansı bacarıqlar lazımdır və kimə uyğundur.",
  },
  ru: {
    navCareers: "Профессии",
    heroDiscover: "Исследовать профессии",
    resultAllCareers: "Смотреть все профессии →",
    hubTitle: "Карта профессий",
    hubSubtitle: (_c, s) =>
      `${s} сфер · на 3 языках. О каждой профессии: чем занимается, какие навыки нужны, путь обучения и кому подходит.`,
    searchPlaceholder: "Найти профессию: например, «дата-аналитик»",
    searchButton: "Найти",
    spheresHeading: "По сферам",
    seeSphere: "Открыть сферу →",
    professionsWord: (n) => `${n} проф.`,
    hubCtaTitle: "Какие профессии подходят именно тебе?",
    hubCtaBody: "Пройди диагностику Yolutap — на карте подсветятся твои профессии.",
    takeTest: "Пройти тест",
    breadcrumbRoot: "Профессии",
    sphereStagedNote: "Подробные страницы профессий добавляются поэтапно.",
    sphereCtaTitle: "Подходит ли тебе эта сфера?",
    sphereCtaBody: "Пройди диагностику — увидишь процент совпадения по каждой профессии.",
    searchNoResults: "Ничего не найдено",
    searchNoResultsHint: "Попробуйте другое написание",
    searchInSphere: "в сфере",
    indexHeading: "Все страницы профессий",
    indexNote:
      "Профессии, у которых есть подробная страница. Список пополняется постепенно — остальные перечислены на странице своей сферы.",
    footerCareers: "Профессии",
    metaTitleHub: "Профессии · Карта профессий — Yolutap",
    metaDescHub:
      "Профессии в 10 сферах, на 3 языках. По каждой профессии: чем занимается, какие навыки нужны и кому подходит.",
  },
  en: {
    navCareers: "Careers",
    heroDiscover: "Explore careers",
    resultAllCareers: "See all careers →",
    hubTitle: "Career map",
    hubSubtitle: (_c, s) =>
      `${s} fields · in 3 languages. For every career: what you do, which skills you need, the learning path and who it fits.`,
    searchPlaceholder: "Search a career: e.g. “data analyst”",
    searchButton: "Search",
    spheresHeading: "By field",
    seeSphere: "Open field →",
    professionsWord: (n) => `${n} careers`,
    hubCtaTitle: "Which careers are right for you?",
    hubCtaBody: "Take the Yolutap diagnostic — your careers will light up on the map.",
    takeTest: "Take the test",
    breadcrumbRoot: "Careers",
    sphereStagedNote: "Detailed career pages are being added in stages.",
    sphereCtaTitle: "Is this field right for you?",
    sphereCtaBody: "Take the diagnostic — you'll see a match score for every career.",
    searchNoResults: "No results",
    searchNoResultsHint: "Try a different spelling",
    searchInSphere: "in",
    indexHeading: "All career pages",
    indexNote:
      "Careers that already have a detail page. The list grows in stages — the rest are listed on their field page.",
    footerCareers: "Careers",
    metaTitleHub: "Careers · Career map — Yolutap",
    metaDescHub:
      "Careers across 10 fields, in 3 languages. For each career: what you do, which skills you need and who it fits.",
  },
};

// ── Stage 2 detail-page strings (per locale) ────────────────────────────
// Section headings are question-form for SEO. Name-embedding headings take the
// localized profession name. RU/EN are proposals pending translation review.
export type CareerPageStrings = {
  tasksHeading: (name: string) => string;
  skillsHeading: string;
  pathHeading: (name: string) => string;
  envHeading: string;
  similarHeading: string;
  // sticky anchor-menu labels (short)
  anchorTasks: string;
  anchorSkills: string;
  anchorPath: string;
  anchorEnv: string;
  // locked compatibility block
  lockTitle: string;
  lockBody: string;
  takeTest: string;
  // snapshot fact labels
  factSphere: string;
  factEdu: string;
  factEnv: string;
  factRiasec: string;
  riasecTooltip: string;
  // sponsor slot
  sponsorTag: string;
  sponsorBody: string;
  sponsorCta: string;
  // similar-careers card link
  similarSee: string;
  // the one live link under the similar block — the whole sphere
  similarAll: (sphere: string) => string;
};

export const CAREER_PAGE_UI: Record<Lang, CareerPageStrings> = {
  az: {
    tasksHeading: (n) => `${n} nə ilə məşğul olur?`,
    skillsHeading: "Hansı bacarıqlar lazımdır?",
    pathHeading: (n) => `${n} olmaq üçün hansı yol keçmək lazımdır?`,
    envHeading: "İş mühiti necədir?",
    similarHeading: "Oxşar peşələr",
    anchorTasks: "Nə edir",
    anchorSkills: "Bacarıqlar",
    anchorPath: "Yol",
    anchorEnv: "İş mühiti",
    lockTitle: "Bu peşə sənə uyğundurmu?",
    lockBody: "Uyğunluq faizini görmək üçün diaqnostikanı keç.",
    takeTest: "Testi keç",
    factSphere: "Sahə",
    factEdu: "Təhsil yolu",
    factEnv: "İş mühiti",
    factRiasec: "Maraq profili",
    riasecTooltip: "maraq profili (RIASEC)",
    sponsorTag: "SPONSOR YERİ",
    sponsorBody:
      "Bu yer sizin şirkətiniz üçün ayrılıb: peşəni brendiniz, videolarınız və real mütəxəssislərinizlə təqdim edin.",
    sponsorCta: "Sponsor olun →",
    similarSee: "Bax →",
    similarAll: (s) => `«${s}» sahəsinin bütün peşələri →`,
  },
  ru: {
    tasksHeading: (n) => `${n}: чем занимается?`,
    skillsHeading: "Какие навыки нужны?",
    pathHeading: (n) => `${n}: какой путь пройти?`,
    envHeading: "Какая рабочая среда?",
    similarHeading: "Похожие профессии",
    anchorTasks: "Чем занят",
    anchorSkills: "Навыки",
    anchorPath: "Путь",
    anchorEnv: "Среда",
    lockTitle: "Подходит ли тебе эта профессия?",
    lockBody: "Пройди диагностику, чтобы увидеть процент совместимости.",
    takeTest: "Пройти тест",
    factSphere: "Сфера",
    factEdu: "Путь обучения",
    factEnv: "Рабочая среда",
    factRiasec: "Профиль интересов",
    riasecTooltip: "профиль интересов (RIASEC)",
    sponsorTag: "МЕСТО СПОНСОРА",
    sponsorBody:
      "Это место отведено для вашей компании: представьте профессию своим брендом, видео и реальными специалистами.",
    sponsorCta: "Стать спонсором →",
    similarSee: "Смотреть →",
    similarAll: (s) => `Все профессии сферы «${s}» →`,
  },
  en: {
    tasksHeading: (n) => `What does a ${n} do?`,
    skillsHeading: "Which skills do you need?",
    pathHeading: (n) => `How to become a ${n}?`,
    envHeading: "What is the work environment like?",
    similarHeading: "Similar careers",
    anchorTasks: "What they do",
    anchorSkills: "Skills",
    anchorPath: "Path",
    anchorEnv: "Environment",
    lockTitle: "Is this career right for you?",
    lockBody: "Take the diagnostic to see your compatibility score.",
    takeTest: "Take the test",
    factSphere: "Field",
    factEdu: "Education path",
    factEnv: "Work environment",
    factRiasec: "Interest profile",
    riasecTooltip: "interest profile (RIASEC)",
    sponsorTag: "SPONSOR SPOT",
    sponsorBody:
      "This spot is reserved for your company: present the career with your brand, videos and real specialists.",
    sponsorCta: "Become a sponsor →",
    similarSee: "View →",
    similarAll: (s) => `All careers in ${s} →`,
  },
};

// entry tag → human-readable "Təhsil yolu" (education path). Pending review.
export const ENTRY_LABEL: Record<Lang, Record<string, string>> = {
  az: {
    bachelor: "Bakalavr",
    master: "Magistratura",
    certificate: "Sertifikat / kurslar",
    diploma: "Diplom / kollec",
    medical_degree: "Tibb təhsili",
    military_academy: "Hərbi akademiya",
    flight_school: "Uçuş məktəbi",
    experience: "Təcrübə ilə",
    talent: "İstedad / portfolio",
    none: "Xüsusi tələb yoxdur",
  },
  ru: {
    bachelor: "Бакалавриат",
    master: "Магистратура",
    certificate: "Сертификат / курсы",
    diploma: "Диплом / колледж",
    medical_degree: "Медицинское образование",
    military_academy: "Военная академия",
    flight_school: "Лётная школа",
    experience: "Через опыт",
    talent: "Талант / портфолио",
    none: "Особых требований нет",
  },
  en: {
    bachelor: "Bachelor’s degree",
    master: "Master’s degree",
    certificate: "Certificate / courses",
    diploma: "Diploma / college",
    medical_degree: "Medical degree",
    military_academy: "Military academy",
    flight_school: "Flight school",
    experience: "Through experience",
    talent: "Talent / portfolio",
    none: "No formal requirement",
  },
};

// environment_tag → short "İş mühiti" label for the snapshot. Pending review.
export const ENV_LABEL: Record<Lang, Record<string, string>> = {
  az: { office: "Ofis", field: "Sahə / açıq hava", mixed: "Ofis / sahə", shift: "Növbəli iş", remote: "Ofis / remote", institution: "Məktəb / qurum" },
  ru: { office: "Офис", field: "Поле / выезд", mixed: "Офис / поле", shift: "Сменный график", remote: "Офис / удалёнка", institution: "Школа / учреждение" },
  en: { office: "Office", field: "Field / outdoors", mixed: "Office / field", shift: "Shift work", remote: "Office / remote", institution: "School / institution" },
};

const ruCareers = (n: number) => {
  const a = n % 100;
  const b = n % 10;
  if (a > 10 && a < 20) return "профессий";
  if (b === 1) return "профессия";
  if (b >= 2 && b <= 4) return "профессии";
  return "профессий";
};

/**
 * Description страницы сферы (16.10.2026): одно нормальное предложение с
 * заглавной буквы. Было «Технологии и IT — 25 проф.. цифровые продукты…».
 */
export function sphereDescription(lang: Lang, name: string, count: number, subtitle: string): string {
  if (lang === "az")
    return `${name}: ${count} peşə — ${subtitle}. Peşələrin təsviri və test — Yolutap peşə xəritəsində.`;
  if (lang === "ru")
    return `${name}: ${count} ${ruCareers(count)} — ${subtitle}. Описания и тест — на карте профессий Yolutap.`;
  return `${name}: ${count} careers in ${subtitle}. Descriptions and a test on the Yolutap career map.`;
}
